import { HttpClient, HttpParams } from '@angular/common/http';
import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import FileSaver from 'file-saver';
import { APIConnectionProfile, APIConnectionProfileScreen } from 'src/app/model/screen/api-connection-profile-screen';
import { ApiService, Response } from 'src/app/service/api.service';
import { SandboxPageStateService } from 'src/app/service/sandbox-page-state.service';

@Component({
  selector: 'app-api-connection-profile-screen',
  templateUrl: './api-connection-profile-screen.component.html',
  styleUrls: ['./api-connection-profile-screen.component.scss']
})
export class ApiConnectionProfileScreenComponent implements OnInit {
  @ViewChild('inputFileElement')
  inputFileElement!: ElementRef;

  TRIAL = 'TRIAL';
  ADD_NEW_CONNECTION = 'ADD NEW CONNECTION';

  EXTERNAL_PROFILE_APIS = ['Pre-Live', 'Beta'];
  INTERNAL_PROFILE_APIS = ['Pre-Live', 'Beta', 'Custom'];
  ADMIN_PROFILE_APIS = ['Pre-Live', 'Beta', 'Live', 'Custom'];
  HELPDESK_PROFILE_APIS = ['Pre-Live', 'Beta', 'Live', 'Custom'];

  apiConnectionProfileURIs: string[];
  apiURIs: string[];

  percentDone!: number;
  uploadSuccess!: boolean;

  apiConnectionProfileForm: FormGroup;

  apiConnectionProfileScreen: APIConnectionProfileScreen = {} as APIConnectionProfileScreen;
  apiResponse: Response = {} as Response;
  constructor(private apiService: ApiService, private httpClient: HttpClient, private formBuilder: FormBuilder, private sandboxPageStateService: SandboxPageStateService) {
    this.apiConnectionProfileURIs = this.sandboxPageStateService.getConfig().apiConnectionProfileURIs.map((apiConnectionProfileURI: { api_id: any }) => apiConnectionProfileURI.api_id);
    this.apiURIs = this.apiConnectionProfileURIs.filter((apiURI) => this.EXTERNAL_PROFILE_APIS.includes(apiURI));
    this.apiConnectionProfileForm = new FormGroup({
      apiConnectionProfileName: new FormControl(this.TRIAL, [Validators.required, Validators.minLength(1), Validators.maxLength(30), Validators.pattern(/^[a-zA-Z0-9]*$/)]),
      connectionName: new FormControl('', [Validators.required, Validators.minLength(1), Validators.maxLength(30)]),
      apiURI: new FormControl(this.sandboxPageStateService.getConfig().trialAPIConnectionProfile.apiURI, [Validators.required, Validators.minLength(1), Validators.maxLength(30)]),
      apiURICustom: new FormControl('', [Validators.minLength(1), Validators.maxLength(30), Validators.pattern(/^[a-zA-Z0-9]*$/)]),
      clientKey: new FormControl('', [Validators.required, Validators.minLength(1), Validators.maxLength(50), Validators.pattern(/^[a-zA-Z0-9-]*$/)]),
      corporateID: new FormControl('', [Validators.minLength(1), Validators.maxLength(30), Validators.pattern(/^[a-zA-Z0-9]*$/)]),
      includeAggregator: new FormControl(''),
      aggregatorID: new FormControl('', [Validators.minLength(1), Validators.maxLength(30), Validators.pattern(/^[a-zA-Z0-9]*$/)]),
      aggregatorName: new FormControl('', [Validators.minLength(1), Validators.maxLength(30), Validators.pattern(/^[a-zA-Z0-9 ]*$/)]),
      corporateSender: new FormControl(''),
      directCorporate: new FormControl(''),
      iata: new FormControl('', [Validators.required, Validators.minLength(1), Validators.maxLength(30), Validators.pattern(/^[a-zA-Z0-9]*$/)]),
      agencyName: new FormControl('', [Validators.required, Validators.minLength(1), Validators.maxLength(30), Validators.pattern(/^[a-zA-Z0-9 ]*$/)]),
      contactEMail: new FormControl('', [Validators.required, Validators.email, Validators.minLength(1), Validators.maxLength(30)])
    });
  }

  showPassword: boolean = false;

  showAddNewConnection: boolean = false;
  connectionNames: string[] = [];
  showHideChooseFile: boolean = false;
  showHideConnectionButton: boolean = false;

  submitted = false;

  ngOnInit(): void {
    //console.log('user: ',this.sandboxPageStateService.getUser());

    this.showPassword = false;

    if (this.sandboxPageStateService.getUser().isAdminUser) {
      this.apiURIs = this.apiConnectionProfileURIs.filter((apiURI) => this.ADMIN_PROFILE_APIS.includes(apiURI));
    } else {
      if ((this.sandboxPageStateService.getUser().userEmail.includes('@ba.com') && this.sandboxPageStateService.getUser().isSMEUser) || this.sandboxPageStateService.getUser().isSMEUser) {
        this.apiURIs = this.apiConnectionProfileURIs.filter((apiURI) => this.HELPDESK_PROFILE_APIS.includes(apiURI));
      } else if (this.sandboxPageStateService.getUser().userEmail.includes('@ba.com')) {
        this.apiURIs = this.apiConnectionProfileURIs.filter((apiURI) => this.INTERNAL_PROFILE_APIS.includes(apiURI));
      } else {
        this.apiURIs = this.apiConnectionProfileURIs.filter((apiURI) => this.EXTERNAL_PROFILE_APIS.includes(apiURI));
      }
    }

    this.apiConnectionProfileScreen = {
      activeConnectionName: this.sandboxPageStateService.getActiveAPIConnectionProfile().connectionName,
      apiConnectionProfiles: this.sandboxPageStateService.getAPIConnectionProfiles()
    };

    this.connectionNames.push(this.TRIAL);

    if (this.apiConnectionProfileScreen.apiConnectionProfiles.length > 0) {
      this.apiConnectionProfileScreen.apiConnectionProfiles!.forEach((apiConnectionProfile) => this.connectionNames.push(apiConnectionProfile.connectionName));
    }
    this.connectionNames.push(this.ADD_NEW_CONNECTION);

    this.apiConnectionProfileForm.controls['apiConnectionProfileName'].setValue(this.apiConnectionProfileScreen.activeConnectionName);
  }

  isCorporateSender() {
    if (!this.apiConnectionProfileForm.value.corporateSender) {
      this.apiConnectionProfileForm.controls['corporateSender'].setValue(true);
    }
    this.apiConnectionProfileForm.controls['directCorporate'].setValue(false);
  }

  isDirectCorporate() {
    if (!this.apiConnectionProfileForm.value.directCorporate) {
      this.apiConnectionProfileForm.controls['directCorporate'].setValue(true);
    }
    this.apiConnectionProfileForm.controls['corporateSender'].setValue(false);
  }

  isApiURICustom() {
    return this.apiConnectionProfileForm.value.apiURI === 'Custom';
  }

  isAddNewConnection() {
    return this.apiConnectionProfileForm.value.apiConnectionProfileName === this.ADD_NEW_CONNECTION;
  }

  upload(event: any) {
    if (event.target.files.length !== 1) {
      console.error('No file selected');
    } else {
      // console.log('importing file : ', event.target.files[0]);
      const reader = new FileReader();
      reader.readAsText(event.target.files[0]);
      reader.onloadend = (e) => {
        const data: string = reader.result!.toString();
        try {
          // console.log('data to be imported : ', this.getDecodedBase64String(data));
          const importedProfiles: APIConnectionProfile[] = JSON.parse(this.sandboxPageStateService.getDecodedBase64String(data));

          if (importedProfiles.length + this.connectionNames.length - 2 <= this.sandboxPageStateService.getConfig().maxNumberOfAPIConnectionProfiles) {
            importedProfiles.forEach((profile) => {
              const existingMatchingConnectionNameIndex: number = this.apiConnectionProfileScreen.apiConnectionProfiles.findIndex(
                (apiConnectionProfile) => apiConnectionProfile.connectionName === profile.connectionName
              );
              if (existingMatchingConnectionNameIndex > -1) {
                this.apiConnectionProfileScreen.apiConnectionProfiles.splice(existingMatchingConnectionNameIndex, 1);
              }
            });
            this.apiConnectionProfileScreen.apiConnectionProfiles.push(...importedProfiles);
            this.connectionNames = [];
            this.connectionNames.push(this.TRIAL);
            if (this.apiConnectionProfileScreen.apiConnectionProfiles.length > 0) {
              this.apiConnectionProfileScreen.apiConnectionProfiles!.forEach((apiConnectionProfile) => this.connectionNames.push(apiConnectionProfile.connectionName));
            }
            this.connectionNames.push(this.ADD_NEW_CONNECTION);

            localStorage.setItem(this.sandboxPageStateService.getEncodedUserEmail(), this.sandboxPageStateService.getEncodedBase64String(JSON.stringify(this.apiConnectionProfileScreen)));

            this.showHideChooseFile = false;
            this.uploadSuccess = true;
            this.inputFileElement.nativeElement.value = '';
          } else {
            alert('Maximum number of connection after import can be ' + this.sandboxPageStateService.getConfig().maxNumberOfAPIConnectionProfiles + '.\nAborting import !');
          }
        } catch (error) {
          alert('Error while importing profiles. Please use a valid file.');
          this.showHideChooseFile = true;
          this.uploadSuccess = false;
          this.inputFileElement.nativeElement.value = '';
        }
      };
    }
  }

  closePannel() {
    let patchSandboxSourceData = { hideSideMenu: 'Yes' };
    this.sandboxPageStateService.updateSandboxDataSource(patchSandboxSourceData);
  }

  getProfile() {
    this.showPassword = false;

    if (![this.TRIAL].includes(this.apiConnectionProfileForm.value.apiConnectionProfileName)) {
      let activeAPIConnectionProfile = this.apiConnectionProfileScreen.apiConnectionProfiles.find(
        (apiConnectionProfile) => apiConnectionProfile.connectionName === this.apiConnectionProfileScreen.activeConnectionName
      );

      if ([this.ADD_NEW_CONNECTION].includes(this.apiConnectionProfileForm.value.apiConnectionProfileName)) {
        activeAPIConnectionProfile = this.getEmptyAPIConnectionProfileData();
      }

      if (activeAPIConnectionProfile) {
        this.apiConnectionProfileForm.controls['connectionName'].setValue(activeAPIConnectionProfile!.connectionName);
        this.apiConnectionProfileForm.controls['apiURI'].setValue(activeAPIConnectionProfile!.apiURI);
        this.apiConnectionProfileForm.controls['apiURICustom'].setValue(activeAPIConnectionProfile!.apiURICustom);
        this.apiConnectionProfileForm.controls['clientKey'].setValue(activeAPIConnectionProfile!.clientKey);
        this.apiConnectionProfileForm.controls['corporateID'].setValue(activeAPIConnectionProfile!.corporateID);
        this.apiConnectionProfileForm.controls['includeAggregator'].setValue(activeAPIConnectionProfile!.tmcDetails.includeAggregator);
        this.apiConnectionProfileForm.controls['aggregatorID'].setValue(activeAPIConnectionProfile!.aggregatorID);
        this.apiConnectionProfileForm.controls['aggregatorName'].setValue(activeAPIConnectionProfile!.aggregatorName);
        this.apiConnectionProfileForm.controls['corporateSender'].setValue(activeAPIConnectionProfile!.corporateSenderDetails.corporateSender);
        this.apiConnectionProfileForm.controls['directCorporate'].setValue(activeAPIConnectionProfile!.corporateSenderDetails.directCorporate);
        this.apiConnectionProfileForm.controls['iata'].setValue(activeAPIConnectionProfile!.iata);
        this.apiConnectionProfileForm.controls['agencyName'].setValue(activeAPIConnectionProfile!.agencyName);
        this.apiConnectionProfileForm.controls['contactEMail'].setValue(activeAPIConnectionProfile!.contactEMail);
        this.showAddNewConnection = true;
      } else {
        // console.log('Expected matching API connection profile for connection name : ', this.apiConnectionProfileForm.value.apiConnectionProfileName);
      }
    } else {
      alert('Cannot Edit TRIAL Connection');
    }
  }

  setProfile(event: any, updateOcn?: boolean) {
    this.showPassword = false;
    if (event.target.value === this.ADD_NEW_CONNECTION) {
      if (this.connectionNames.length - 2 < this.sandboxPageStateService.getConfig().maxNumberOfAPIConnectionProfiles) {
        this.showAddNewConnection = true;
        this.getProfile();
      } else {
        this.apiConnectionProfileForm.controls['apiConnectionProfileName'].setValue(this.TRIAL);
        this.showAddNewConnection = false;
        alert('Maximum number of connection can be ' + this.sandboxPageStateService.getConfig().maxNumberOfAPIConnectionProfiles);
      }
    } else if (event.target.value === this.TRIAL) {
      this.showAddNewConnection = false;
      this.sandboxPageStateService.resetActiveAPIConnectionProfile();
      this.apiConnectionProfileScreen.activeConnectionName = this.sandboxPageStateService.getActiveAPIConnectionProfile().connectionName;
    } else {
      this.showAddNewConnection = false;
      this.apiConnectionProfileScreen.activeConnectionName = event.target.value;
      this.apiConnectionProfileForm.controls['connectionName'].setValue(this.apiConnectionProfileScreen.activeConnectionName);

      let activeAPIConnectionProfile = this.apiConnectionProfileScreen.apiConnectionProfiles.find(
        (apiConnectionProfile) => apiConnectionProfile.connectionName === this.apiConnectionProfileScreen.activeConnectionName
      );

      if (activeAPIConnectionProfile) {
        this.apiConnectionProfileScreen.activeConnectionName = activeAPIConnectionProfile.connectionName;
      }
    }

    localStorage.setItem(this.sandboxPageStateService.getEncodedUserEmail(), this.sandboxPageStateService.getEncodedBase64String(JSON.stringify(this.apiConnectionProfileScreen)));

    // Removing OCN Call on change of Profile
    // if (updateOcn) {
    //   this.getOcnMessages();
    // }
  }
  async getOcnMessages() {
    const apiConnectionProfile = this.sandboxPageStateService.getActiveAPIConnectionProfile();
    let aggregatorId: any = apiConnectionProfile.aggregatorID;
    let iataNum: any = apiConnectionProfile.iata;
    let queryParams = new HttpParams();
    queryParams = queryParams.append('aggregator_id', aggregatorId);
    queryParams = queryParams.append('iata_number', iataNum);
    queryParams = queryParams.append('status', 'PENDING');
    this.sandboxPageStateService.updateDistruptedPnrCount('0');
    this.apiResponse = await this.apiService.sendGetRequest('/read-ocn-messages', queryParams);
    if (this.apiResponse.success) {
      let distruptedPnrCount: any = 0;
      if (typeof distruptedPnrCount === 'undefined') {
        distruptedPnrCount = 0;
      } else {
        let ocnMessageData: any = this.apiResponse.success;
        ocnMessageData.forEach((ocnMessageDetail: any, i: number) => {
          if (ocnMessageDetail.status === 'PENDING') {
            distruptedPnrCount++;
          }
        });
      }
      this.sandboxPageStateService.updateDistruptedPnrCount(distruptedPnrCount);
    }
  }
  get formControls(): { [key: string]: AbstractControl } {
    return this.apiConnectionProfileForm.controls;
  }

  getAPIConnectionProfileData(): APIConnectionProfile {
    this.submitted = true;

    if (
      this.apiConnectionProfileForm.controls['connectionName'].invalid ||
      this.apiConnectionProfileForm.controls['apiURI'].invalid ||
      this.apiConnectionProfileForm.controls['apiURICustom'].invalid ||
      this.apiConnectionProfileForm.controls['clientKey'].invalid ||
      this.apiConnectionProfileForm.controls['agencyName'].invalid ||
      this.apiConnectionProfileForm.controls['iata'].invalid ||
      this.apiConnectionProfileForm.controls['contactEMail'].invalid ||
      this.apiConnectionProfileForm.controls['corporateID'].invalid ||
      this.apiConnectionProfileForm.controls['aggregatorID'].invalid ||
      this.apiConnectionProfileForm.controls['aggregatorName'].invalid
    ) {
      return {} as APIConnectionProfile;
    } else {
      return {
        connectionName: this.apiConnectionProfileForm.value.connectionName,
        apiURI: this.apiConnectionProfileForm.value.apiURI,
        apiURICustom: this.apiConnectionProfileForm.value.apiURICustom,
        clientKey: this.apiConnectionProfileForm.value.clientKey,
        agencyName: this.apiConnectionProfileForm.value.agencyName,
        iata: this.apiConnectionProfileForm.value.iata,
        contactEMail: this.apiConnectionProfileForm.value.contactEMail,
        corporateSenderDetails: {
          corporateSender: this.apiConnectionProfileForm.value.corporateSender,
          directCorporate: this.apiConnectionProfileForm.value.directCorporate
        },
        corporateID: this.apiConnectionProfileForm.value.corporateID,
        tmcDetails: {
          includeAggregator: this.apiConnectionProfileForm.value.includeAggregator
        },
        aggregatorID: this.apiConnectionProfileForm.value.aggregatorID,
        aggregatorName: this.apiConnectionProfileForm.value.aggregatorName
      };
    }
  }

  getEmptyAPIConnectionProfileData(): APIConnectionProfile {
    this.submitted = false;
    return {
      connectionName: '',
      apiURI: '',
      apiURICustom: '',
      clientKey: '',
      agencyName: '',
      iata: '',
      contactEMail: '',
      corporateSenderDetails: {
        corporateSender: false,
        directCorporate: false
      },
      corporateID: '',
      tmcDetails: {
        includeAggregator: false
      },
      aggregatorID: '',
      aggregatorName: ''
    };
  }

  deleteAPIConnectionProfile(updateOcn?: boolean) {
    let activeAPIConnectionProfile = this.apiConnectionProfileScreen.apiConnectionProfiles.find(
      (apiConnectionProfile) => apiConnectionProfile.connectionName === this.apiConnectionProfileScreen.activeConnectionName
    );
    const index = this.apiConnectionProfileScreen.apiConnectionProfiles.indexOf(activeAPIConnectionProfile!);
    if (index > -1) {
      this.apiConnectionProfileScreen.apiConnectionProfiles.splice(index, 1);
      const cnIndex = this.connectionNames.indexOf(this.apiConnectionProfileScreen.activeConnectionName);
      if (cnIndex > -1) {
        this.connectionNames.splice(cnIndex, 1);
      }
      this.apiConnectionProfileForm.controls['apiConnectionProfileName'].setValue(this.TRIAL);
      this.showAddNewConnection = false;
    }

    localStorage.setItem(this.sandboxPageStateService.getEncodedUserEmail(), this.sandboxPageStateService.getEncodedBase64String(JSON.stringify(this.apiConnectionProfileScreen)));

    this.apiConnectionProfileForm.controls['apiConnectionProfileName'].setValue(this.TRIAL);
    this.setProfile({ target: { value: this.TRIAL } });
    // Removing OCN Call on change of Profile
    // if (updateOcn) {
    //   this.getOcnMessages();
    // }
  }

  updateAPIConnectionProfile() {
    const tempAPIConnectionProfile = this.getAPIConnectionProfileData();

    if (tempAPIConnectionProfile && tempAPIConnectionProfile.connectionName) {
      if (this.connectionNames.includes(tempAPIConnectionProfile.connectionName) && this.apiConnectionProfileScreen.activeConnectionName !== tempAPIConnectionProfile.connectionName) {
        alert('Connection name already exists. Please choose a different connection name.');
      } else {
        this.deleteAPIConnectionProfile();
        this.saveAsNewAPIConnectionProfile();

        localStorage.setItem(this.sandboxPageStateService.getEncodedUserEmail(), this.sandboxPageStateService.getEncodedBase64String(JSON.stringify(this.apiConnectionProfileScreen)));
        // Removing OCN Call on change of Profile
        // this.getOcnMessages();
      }
    }
  }

  saveAsNewAPIConnectionProfile(updateOcn?: boolean) {
    if (this.connectionNames.length - 2 < this.sandboxPageStateService.getConfig().maxNumberOfAPIConnectionProfiles) {
      const tempAPIConnectionProfile = this.getAPIConnectionProfileData();

      if (tempAPIConnectionProfile && tempAPIConnectionProfile.connectionName) {
        if (this.connectionNames.includes(tempAPIConnectionProfile.connectionName)) {
          alert('Connection name already exists. Please choose a different connection name.');
        } else {
          this.apiConnectionProfileScreen.activeConnectionName = tempAPIConnectionProfile.connectionName;
          this.apiConnectionProfileScreen.apiConnectionProfiles.push(tempAPIConnectionProfile);

          localStorage.setItem(this.sandboxPageStateService.getEncodedUserEmail(), this.sandboxPageStateService.getEncodedBase64String(JSON.stringify(this.apiConnectionProfileScreen)));

          const lastEntry = this.connectionNames.pop();
          this.connectionNames.push(this.apiConnectionProfileScreen.activeConnectionName);
          this.connectionNames.push(lastEntry!);
          this.apiConnectionProfileForm.controls['apiConnectionProfileName'].setValue(this.apiConnectionProfileScreen.activeConnectionName);
          this.setProfile({ target: { value: this.apiConnectionProfileScreen.activeConnectionName } });
          // Removing OCN Call on change of Profile
          // if (updateOcn) {
          //   this.getOcnMessages();
          // }
        }
      }
    } else {
      alert('Maximum number of connection can be ' + this.sandboxPageStateService.getConfig().maxNumberOfAPIConnectionProfiles);
    }
  }

  importProfiles() {
    this.showHideChooseFile = true;
    this.uploadSuccess = false;
  }

  exportProfiles() {
    this.showHideChooseFile = false;
    const apiConnectionProfilesData = this.apiConnectionProfileScreen.apiConnectionProfiles.filter((apiConnectionProfile) => apiConnectionProfile.connectionName !== this.TRIAL);

    if (apiConnectionProfilesData && apiConnectionProfilesData.length > 0) {
      var blob = new Blob([this.sandboxPageStateService.getEncodedBase64String(JSON.stringify(apiConnectionProfilesData))], { type: 'text/plain' });
      FileSaver.saveAs(blob, 'sandbox.profile');
    } else {
      alert('No profile to export');
    }
  }
}
